import { Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, {CSSProperties, FC} from 'react';
import {
    ClassStylable,
    TypographyAlignmentProp,
    TypographyVariantProp
} from "../../../models/common/ComponentBehavioural";
import {VisibilityHandler} from "../VisibilityHandler";
import {ReactChildren} from "../../../models/common/ReactChildren";

/**
 * Represents the properties for configuring a text display component.
 *
 * This interface ensures consistency in how text is displayed across various UI components,
 * supporting flexible alignment, styling, and interaction behaviors to meet design and user requirements.
 *
 * Extends:
 * - `ReactChildren`: Provides support for rendering React child components.
 * - `TypographyVariantProp`: Allows specification of predefined typography variants.
 * - `ClassStylable`: Enables customization of CSS class styling.
 * - `TypographyAlignmentProp`: Facilitates alignment of text in a consistent manner.
 */
export interface TextDisplayProps extends ReactChildren,TypographyVariantProp,  ClassStylable, TypographyAlignmentProp {
    onClick?: () => void
    /** component can display text horizontally and vertically. defaults to horizontal */
    orientation?: "horizontal" | "vertical"
    textColor?: string
    style?: CSSProperties
    bold?: boolean
}

/**
 * Defines and applies a consistent styling configuration for UI components.
 *
 * This variable provides a centralized way to manage styling rules for specific elements,
 * ensuring design consistency across the application. By using a predefined set of styles,
 * it helps developers enforce uniform behavior and appearance for individual UI elements like typography.
 *
 * @constant
 * @type {Function}
 * @returns {Object} A collection of style rules. Each key represents a specific style class
 *                   or usage context, and its value defines the associated CSS properties.
 * @throws Will not throw exceptions directly, but incorrect usage of `useStyle` in components
 *         can lead to unexpected rendering issues or layout inconsistencies.
 */
const useStyle = makeStyles( _ => ({
    body2: {
        whiteSpace: 'pre-line'
    }
}));

/**
 * Renders text content in either horizontal or vertical orientation with customizable styles and behaviors.
 * Designed for use in user interfaces requiring dynamic text presentation with optional interactivity.
 *
 * The method automatically adjusts behavior based on the `orientation` prop and handles edge cases
 * where vertical orientation requires the `children` to be a string.
 *
 * IMPORTANT:
 *      When orientation is vertical, the children component needs to be a string.
 *      this is checked and an error is thrown, when vertical and children is not a string
 *
 * @param variant The typography variant to be used for text rendering. Defaults to 'body1' if not provided.
 * @param textColor The color of the text, specified as a color name or value. Defaults to 'common.black'.
 * @param children The content to be displayed. Must be a string if the `orientation` is set to 'vertical'.
 * @param className Optional CSS class name for custom styling.
 * @param onClick Optional click event handler for adding interactive behavior.
 * @param orientation Specifies the text layout: 'horizontal' (default) or 'vertical'.
 * @param align The alignment of the text. Accepts standard alignment values (e.g., 'left', 'center', 'right').
 * @param style Optional inline styling applied to the text content or its container.
 *
 * @return A JSX element that renders the text content. Includes dynamic layouts for horizontal or vertical orientations.
 * @throws {Error} Throws an error if `orientation` is set to 'vertical' and `children` is not a string.
 */
export function TextDisplay({
         variant,
         textColor,
         children,
         className,
         onClick,
        orientation,
        align,
        style
    }: TextDisplayProps
) {
    const classes = useStyle();
    const _orientation = orientation || "horizontal"

    if( orientation === "vertical" && typeof children !== "string" ) {
        throw Error("vertical orientation requires the children to be a string")
    }

  //  if( onClick ) {
        return (
            <>
                <VisibilityHandler isVisible={ _orientation === "horizontal" }>
                    <Typography
                        variant={variant || 'body1'}
                        color={textColor || 'common.black'}
                        align={ align }
                        className={className || ''}
                        onClick={ onClick }
                        classes={ classes }
                        style={ style }
                    >
                        {children}
                    </Typography>
                </VisibilityHandler>
                <VisibilityHandler isVisible={ _orientation === "vertical" }>
                    { typeof children === 'string'
                        ? children
                            .split('')
                            .map( char => (
                                <div key={'txt-vert-'+ char}>
                                    <Typography
                                        style={style}
                                        variant={variant || 'body1'}
                                        color={textColor || 'common.black'}
                                        align={ align }
                                        className={className || ''}
                                        onClick={ onClick }
                                        classes={ classes }
                                    >
                                        { char }
                                    </Typography>
                                </div>
                            ))
                        : ''
                    }
                </VisibilityHandler>
            </>
        );
 /*   } else {
        return (
            <Typography
                variant={variant || 'body1'}
                color={textColor || 'textPrimary'}
                className={className || ''}
                classes={ classes }
            >
                {children}
            </Typography>
        );
    }*/

};